import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  TableSortLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import api from '../api';

const COLORS = ['#00e5ff', '#ff4081', '#ffab00', '#00e676', '#2196f3', '#ff5252', '#7c4dff', '#ffd740'];

const ErrorReports = ({ userId = null }) => {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [aggregatedStats, setAggregatedStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedError, setSelectedError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [groupedErrorInstances, setGroupedErrorInstances] = useState([]);
  const [currentInstanceIndex, setCurrentInstanceIndex] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    errorType: '',
    exceptionType: '',
    platform: '',
    gameVersion: '',
    scene: '',
    resolved: '',
    search: '',
    startDate: '',
    endDate: '',
    sortBy: 'impactedUsers',
    sortOrder: 'DESC'
  });

  // Chart filters
  const [chartFilters, setChartFilters] = useState({
    groupBy: 'day',
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchErrors();
  }, [page, rowsPerPage, filters, userId]);

  useEffect(() => {
    fetchStats();
  }, [chartFilters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/error-reports/filters/options');
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchErrors = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        userId: userId || filters.userId,
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) delete params[key];
      });
      
      const response = await api.get('/error-reports', { params });
      setErrors(response.data.errors);
      setTotal(response.data.total);
      setAggregatedStats(response.data.aggregatedStats);
    } catch (err) {
      console.error('Error fetching errors:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/error-reports/stats', { params: chartFilters });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({
      errorType: '',
      exceptionType: '',
      platform: '',
      gameVersion: '',
      scene: '',
      resolved: '',
      search: '',
      startDate: '',
      endDate: '',
      sortBy: 'impactedUsers',
      sortOrder: 'DESC'
    });
    setPage(0);
  };

  const handleSort = (column) => {
    const isAsc = filters.sortBy === column && filters.sortOrder === 'ASC';
    setFilters({
      ...filters,
      sortBy: column,
      sortOrder: isAsc ? 'DESC' : 'ASC'
    });
    setPage(0);
  };

  const handleRowClick = async (error) => {
    // Fetch all instances of this grouped error
    try {
      const response = await api.get(`/error-reports/group/${error.errorHash}`);
      setGroupedErrorInstances(response.data.errors);
      setCurrentInstanceIndex(0);
      setSelectedError(response.data.errors[0] || error);
      setDialogOpen(true);
    } catch (err) {
      console.error('Error fetching grouped instances:', err);
      // Fallback to showing just this error
      setGroupedErrorInstances([error]);
      setCurrentInstanceIndex(0);
      setSelectedError(error);
      setDialogOpen(true);
    }
  };

  const handlePreviousInstance = () => {
    if (currentInstanceIndex > 0) {
      const newIndex = currentInstanceIndex - 1;
      setCurrentInstanceIndex(newIndex);
      setSelectedError(groupedErrorInstances[newIndex]);
    }
  };

  const handleNextInstance = () => {
    if (currentInstanceIndex < groupedErrorInstances.length - 1) {
      const newIndex = currentInstanceIndex + 1;
      setCurrentInstanceIndex(newIndex);
      setSelectedError(groupedErrorInstances[newIndex]);
    }
  };

  const handleCopyErrorAsJson = (error, e) => {
    e.stopPropagation();
    
    const errorData = {
      id: error.id,
      errorId: error.errorId,
      errorHash: error.errorHash,
      userId: error.userId,
      errorType: error.errorType,
      exceptionType: error.exceptionType,
      message: error.message,
      stackTrace: error.stackTrace,
      errorTimestamp: error.errorTimestamp,
      gameVersion: error.gameVersion,
      scene: error.scene,
      platform: error.platform,
      operatingSystem: error.operatingSystem,
      deviceModel: error.deviceModel,
      deviceType: error.deviceType,
      memoryUsed: error.memoryUsed,
      systemMemorySize: error.systemMemorySize,
      fps: error.fps,
      totalOccurrences: error.totalOccurrences,
      impactedUsers: error.impactedUsers,
      resolved: error.resolved,
      notes: error.notes,
      createdAt: error.createdAt,
      updatedAt: error.updatedAt
    };
    
    navigator.clipboard.writeText(JSON.stringify(errorData, null, 2))
      .then(() => {
        // Could add a snackbar notification here if desired
        console.log('Error copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy:', err);
      });
  };

  const handleResolveToggle = async (errorId, currentResolved) => {
    try {
      await api.put(`/error-reports/${errorId}`, {
        resolved: !currentResolved
      });
      fetchErrors();
      if (selectedError && selectedError.id === errorId) {
        setSelectedError({ ...selectedError, resolved: !currentResolved });
      }
    } catch (err) {
      console.error('Error updating error:', err);
    }
  };

  const getErrorTypeColor = (type) => {
    switch (type) {
      case 'Error': return 'error';
      case 'Exception': return 'error';
      case 'Assert': return 'warning';
      case 'Warning': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {userId ? 'User Error Reports' : 'Error Reports'}
        </Typography>
        <IconButton onClick={() => { fetchErrors(); fetchStats(); }}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {/* Charts Section */}
      {!userId && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={2} mb={3} alignItems="center">
            <Typography variant="h6">Error Analytics</Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Group By</InputLabel>
              <Select
                value={chartFilters.groupBy}
                onChange={(e) => setChartFilters({ ...chartFilters, groupBy: e.target.value })}
                label="Group By"
              >
                <MenuItem value="hour">Hour</MenuItem>
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="month">Month</MenuItem>
              </Select>
            </FormControl>
            <TextField
              type="date"
              label="Start Date"
              value={chartFilters.startDate}
              onChange={(e) => setChartFilters({ ...chartFilters, startDate: e.target.value })}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="End Date"
              value={chartFilters.endDate}
              onChange={(e) => setChartFilters({ ...chartFilters, endDate: e.target.value })}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          {statsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : stats ? (
            <Stack spacing={4}>
              {/* Errors Over Time */}
              <Box sx={{ width: '100%', height: 400 }}>
                <Typography variant="subtitle1" gutterBottom>Errors Over Time</Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={stats.timeSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="period" stroke="#fff" />
                    <YAxis stroke="#fff" />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #00e5ff' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#00e5ff" name="Total Errors" />
                    <Line type="monotone" dataKey="uniqueUsers" stroke="#ff4081" name="Affected Users" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              {/* Error Types and Platforms - side by side */}
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
                <Box sx={{ flex: 1, height: 350 }}>
                  <Typography variant="subtitle1" gutterBottom>Error Types</Typography>
                  <ResponsiveContainer width="100%" height="90%">
                    <BarChart data={stats.errorTypes}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="errorType" stroke="#fff" />
                      <YAxis stroke="#fff" />
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #00e5ff' }}
                      />
                      <Bar dataKey="count" fill="#00e5ff" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                <Box sx={{ flex: 1, height: 350 }}>
                  <Typography variant="subtitle1" gutterBottom>Platforms</Typography>
                  <ResponsiveContainer width="100%" height="90%">
                    <PieChart>
                      <Pie
                        data={stats.platforms}
                        dataKey="count"
                        nameKey="platform"
                        cx="50%"
                        cy="45%"
                        outerRadius={80}
                        label={(entry) => `${entry.platform}: ${entry.count}`}
                      >
                        {stats.platforms.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #00e5ff' }}
                      />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Stack>

              {/* Top Exception Types */}
              <Box sx={{ width: '100%', height: 450 }}>
                <Typography variant="subtitle1" gutterBottom>Top Exception Types</Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={stats.exceptionTypes} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" stroke="#fff" />
                    <YAxis 
                      type="category" 
                      dataKey="exceptionType" 
                      stroke="#fff" 
                      width={300}
                      tick={{ fontSize: 11 }}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #00e5ff' }}
                    />
                    <Bar dataKey="count" fill="#ff4081" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {/* Top Scenes */}
              <Box sx={{ width: '100%', height: 400 }}>
                <Typography variant="subtitle1" gutterBottom>Top Scenes</Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={stats.scenes} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" stroke="#fff" />
                    <YAxis 
                      type="category" 
                      dataKey="scene" 
                      stroke="#fff" 
                      width={150}
                      tick={{ fontSize: 11 }}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #00e5ff' }}
                    />
                    <Bar dataKey="count" fill="#ffab00" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Stack>
          ) : null}
        </Paper>
      )}

      {/* Aggregated Stats Summary */}
      {aggregatedStats && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {aggregatedStats.uniqueErrors.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unique Errors (in filter)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main">
                  {aggregatedStats.totalOccurrences.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Occurrences (in filter)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h4" color="info.main">
                  {aggregatedStats.impactedUsers.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Impacted Users (in filter)
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Filters Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FilterListIcon />
            <Typography variant="h6">Filters</Typography>
            <Button size="small" onClick={handleClearFilters}>Clear All</Button>
          </Stack>
          
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel>Error Type</InputLabel>
                <Select
                  value={filters.errorType}
                  onChange={(e) => handleFilterChange('errorType', e.target.value)}
                  label="Error Type"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.errorTypes?.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={filters.platform}
                  onChange={(e) => handleFilterChange('platform', e.target.value)}
                  label="Platform"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.platforms?.map(platform => (
                    <MenuItem key={platform} value={platform}>{platform}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel>Game Version</InputLabel>
                <Select
                  value={filters.gameVersion}
                  onChange={(e) => handleFilterChange('gameVersion', e.target.value)}
                  label="Game Version"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.gameVersions?.map(version => (
                    <MenuItem key={version} value={version}>{version}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.resolved}
                  onChange={(e) => handleFilterChange('resolved', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="false">Unresolved</MenuItem>
                  <MenuItem value="true">Resolved</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel>Exception Type</InputLabel>
                <Select
                  value={filters.exceptionType}
                  onChange={(e) => handleFilterChange('exceptionType', e.target.value)}
                  label="Exception Type"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.exceptionTypes?.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel>Scene</InputLabel>
                <Select
                  value={filters.scene}
                  onChange={(e) => handleFilterChange('scene', e.target.value)}
                  label="Scene"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.scenes?.map(scene => (
                    <MenuItem key={scene} value={scene}>{scene}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                type="date"
                label="Start Date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                type="date"
                label="End Date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <TextField
              fullWidth
              label="Search (message, stack trace, exception type)"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search errors..."
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Errors Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell>
                  <TableSortLabel
                    active={filters.sortBy === 'errorTimestamp'}
                    direction={filters.sortBy === 'errorTimestamp' ? filters.sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('errorTimestamp')}
                  >
                    Time
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={filters.sortBy === 'errorType'}
                    direction={filters.sortBy === 'errorType' ? filters.sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('errorType')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>Exception</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={filters.sortBy === 'platform'}
                    direction={filters.sortBy === 'platform' ? filters.sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('platform')}
                  >
                    Platform
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={filters.sortBy === 'gameVersion'}
                    direction={filters.sortBy === 'gameVersion' ? filters.sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('gameVersion')}
                  >
                    Version
                  </TableSortLabel>
                </TableCell>
                <TableCell>Scene</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={filters.sortBy === 'totalOccurrences'}
                    direction={filters.sortBy === 'totalOccurrences' ? filters.sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('totalOccurrences')}
                  >
                    Total Occurrences
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={filters.sortBy === 'impactedUsers'}
                    direction={filters.sortBy === 'impactedUsers' ? filters.sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('impactedUsers')}
                  >
                    Impacted Users
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : errors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    No errors found
                  </TableCell>
                </TableRow>
              ) : (
                errors.map((error) => (
                  <React.Fragment key={error.id}>
                    <TableRow
                      hover
                      onClick={() => setExpandedRow(expandedRow === error.id ? null : error.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {expandedRow === error.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(error.errorTimestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={error.errorType} 
                          color={getErrorTypeColor(error.errorType)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {error.exceptionType || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {error.message}
                        </Typography>
                      </TableCell>
                      <TableCell>{error.platform}</TableCell>
                      <TableCell>{error.gameVersion}</TableCell>
                      <TableCell>{error.scene}</TableCell>
                      <TableCell>
                        <Chip label={error.totalOccurrences || 0} size="small" color="warning" />
                      </TableCell>
                      <TableCell>
                        <Chip label={error.impactedUsers || 0} size="small" color="info" />
                      </TableCell>
                      <TableCell>
                        {error.resolved ? (
                          <Chip label="Resolved" color="success" size="small" icon={<CheckIcon />} />
                        ) : (
                          <Chip label="Open" color="error" size="small" icon={<CloseIcon />} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Copy as JSON">
                            <IconButton size="small" onClick={(e) => handleCopyErrorAsJson(error, e)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRowClick(error); }}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={error.resolved ? "Mark as Open" : "Mark as Resolved"}>
                            <IconButton 
                              size="small" 
                              onClick={(e) => { e.stopPropagation(); handleResolveToggle(error.id, error.resolved); }}
                            >
                              {error.resolved ? <CloseIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={12} sx={{ py: 0 }}>
                        <Collapse in={expandedRow === error.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: 'background.default' }}>
                            <Grid container spacing={3}>
                              <Grid item xs={12}>
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Error Message
                                  </Typography>
                                  <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                      {error.message}
                                    </Typography>
                                  </Paper>
                                </Box>
                              </Grid>
                              
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">Stack Trace</Typography>
                                <Paper sx={{ p: 2, mt: 1, bgcolor: 'background.paper' }}>
                                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                                    {error.stackTrace || 'No stack trace available'}
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Stack spacing={1}>
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary">User ID</Typography>
                                    <Typography variant="body2">{error.userId || 'Anonymous'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Total Occurrences</Typography>
                                    <Typography variant="body2">{error.totalOccurrences || 0}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Impacted Users</Typography>
                                    <Typography variant="body2">{error.impactedUsers || 0}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Device</Typography>
                                    <Typography variant="body2">{error.deviceModel} ({error.deviceType})</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary">OS</Typography>
                                    <Typography variant="body2">{error.operatingSystem}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Memory</Typography>
                                    <Typography variant="body2">
                                      {error.memoryUsed ? `${(error.memoryUsed / 1024 / 1024).toFixed(2)} MB / ${error.systemMemorySize} MB` : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary">FPS</Typography>
                                    <Typography variant="body2">{error.fps || 'N/A'}</Typography>
                                  </Box>
                                </Stack>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Error Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Error Details</Typography>
            {groupedErrorInstances.length > 1 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton 
                  size="small" 
                  onClick={handlePreviousInstance}
                  disabled={currentInstanceIndex === 0}
                >
                  <ExpandLessIcon />
                </IconButton>
                <Typography variant="body2">
                  {currentInstanceIndex + 1} / {groupedErrorInstances.length}
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={handleNextInstance}
                  disabled={currentInstanceIndex === groupedErrorInstances.length - 1}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Stack>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedError && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Error ID</Typography>
                <Typography variant="body2" fontFamily="monospace">{selectedError.errorId}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Timestamp</Typography>
                <Typography variant="body2">
                  {new Date(selectedError.errorTimestamp).toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Message</Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {selectedError.message}
                  </Typography>
                </Paper>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Stack Trace</Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                    {selectedError.stackTrace || 'No stack trace available'}
                  </Typography>
                </Paper>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Typography variant="body2">{selectedError.errorType}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Exception Type</Typography>
                  <Typography variant="body2">{selectedError.exceptionType}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Platform</Typography>
                  <Typography variant="body2">{selectedError.platform}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Game Version</Typography>
                  <Typography variant="body2">{selectedError.gameVersion}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Scene</Typography>
                  <Typography variant="body2">{selectedError.scene}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Total Occurrences</Typography>
                  <Typography variant="body2">{selectedError.totalOccurrences || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Impacted Users</Typography>
                  <Typography variant="body2">{selectedError.impactedUsers || 0}</Typography>
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErrorReports;

